import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ShoppingCart, Minus, Plus, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import PageLayout from "./PageLayout";
import { listContent, mediaUrl } from "../lib/api";
import { useCart } from "../context/CartContext";

const fmt = (n) => `$${Number(n).toFixed(2)}`;
const SIZES = ["S", "M", "L", "XL", "2XL"];

export default function ProductDetailPage() {
  const { id } = useParams();
  const { addItem, setIsOpen } = useCart();
  const [product, setProduct] = useState(undefined); // undefined=loading, null=not found
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState("M");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    setSize("M");
    setQty(1);
    listContent("products")
      .then((items) => {
        const p = items.find((x) => x.id === id && x.published);
        setProduct(p || null);
        if (p) setRelated(items.filter((x) => x.published && x.category === p.category && x.id !== id).slice(0, 4));
      })
      .catch(() => setProduct(null));
  }, [id]);

  if (product === undefined) {
    return <PageLayout title="Shop"><div className="py-20" /></PageLayout>;
  }

  if (!product) {
    return (
      <PageLayout title="Product Not Found">
        <div className="max-w-2xl mx-auto px-5 py-20 text-center">
          <p className="font-serif-body text-[#666]">
            Sorry, we couldn't find that product.
          </p>
          <Link to="/shop" className="inline-block mt-6 font-serif-body text-[#0f9fbc] font-bold">
            ← Back to the shop
          </Link>
        </div>
      </PageLayout>
    );
  }

  const handleAdd = () => {
    addItem({ ...product, id: `${product.id}__${size}`, name: `${product.name} — ${size}` }, qty);
    toast.success("Added to cart");
    setIsOpen(true);
  };

  return (
    <PageLayout title="Shop" subtitle={null}>
      <div className="max-w-6xl mx-auto px-5 py-12 md:py-16">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 font-serif-body text-[#768487] hover:text-[#0f9fbc] transition-colors mb-8"
        >
          <ArrowLeft size={18} /> Back to shop
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-start">
          {/* Image */}
          <div className="rounded-[12px] overflow-hidden border border-[#eaeaea] bg-[#f7f9fa]">
            <img src={mediaUrl(product.image)} alt={product.name} className="w-full h-full object-cover" />
          </div>

          {/* Details */}
          <div>
            <span className="font-serif-body text-[12px] uppercase tracking-wide text-[#68c2d5] font-bold">
              {product.category}
            </span>
            <h1 className="font-serif-display text-[#222] text-2xl md:text-[2rem] font-bold mt-1 leading-snug">
              {product.name}
            </h1>
            <p className="font-serif-display text-[#0f9fbc] text-2xl font-bold mt-4">
              {fmt(product.price)}
            </p>
            <p className="font-serif-body text-[#555] text-[15px] mt-5 leading-relaxed">
              {product.description}
            </p>

            {/* Size */}
            <div className="mt-7">
              <p className="font-serif-body text-[#333] text-[14px] font-bold mb-2">Size</p>
              <div className="flex flex-wrap gap-2">
                {SIZES.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`min-w-[46px] py-2 px-3 rounded-md border font-serif-body text-[14px] transition-colors ${
                      size === s
                        ? "bg-[#0f9fbc] border-[#0f9fbc] text-white"
                        : "bg-white border-[#d7e6ea] text-[#555] hover:border-[#0f9fbc]"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity + Add */}
            <div className="mt-7 flex items-center gap-4">
              <div className="flex items-center border border-[#d7e6ea] rounded-md">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="px-3 py-3 text-[#666] hover:text-[#0f9fbc]"
                  aria-label="Decrease quantity"
                >
                  <Minus size={16} />
                </button>
                <span className="px-4 font-serif-body text-[#333] min-w-[36px] text-center">
                  {qty}
                </span>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  className="px-3 py-3 text-[#666] hover:text-[#0f9fbc]"
                  aria-label="Increase quantity"
                >
                  <Plus size={16} />
                </button>
              </div>
              <button
                onClick={handleAdd}
                className="flex-1 flex items-center justify-center gap-2 bg-[#0f9fbc] hover:bg-[#008da9] text-white font-serif-body font-bold py-3 rounded-[4px] transition-colors"
              >
                <ShoppingCart size={18} /> Add to Cart
              </button>
            </div>

            <ul className="mt-8 space-y-2">
              {["Free shipping on orders over $75", "Soft, durable, true-to-size fit", "Ships within 3–5 business days"].map(
                (f) => (
                  <li key={f} className="flex items-center gap-2 font-serif-body text-[14px] text-[#666]">
                    <Check size={16} className="text-[#68c2d5]" /> {f}
                  </li>
                )
              )}
            </ul>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-20">
            <h2 className="font-serif-display text-[#222] text-xl md:text-2xl font-bold mb-6">
              You may also like
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/shop/${p.id}`}
                  className="group rounded-[10px] overflow-hidden border border-[#eaeaea] bg-white flex flex-col transition-all duration-300 hover:shadow-[0_12px_30px_rgba(0,0,0,0.08)]"
                >
                  <div className="aspect-square overflow-hidden bg-[#f7f9fa]">
                    <img
                      src={mediaUrl(p.image)}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif-body text-[#4a4a4a] text-[13px] leading-snug line-clamp-2">
                      {p.name}
                    </h3>
                    <p className="font-serif-display text-[#0f9fbc] text-[16px] font-bold mt-2">
                      {fmt(p.price)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
